import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { getBillUrl, viewBill } from '../lib/api';
import { compressImageFile } from '../lib/imageCompress';
import {
  Wallet, Plus, Eye, Send, ArrowLeft, Trash2, Camera, Upload,
  CheckCircle2, XCircle, AlertTriangle, Clock, ChevronDown,
  FileText, LogOut, Loader2, X, Edit2, MessageSquare, ChevronUp,
  Users, TrendingUp, BarChart3, ChevronRight, User, ArrowRightLeft, HandCoins
} from 'lucide-react';

const STATUS_CONFIG = {
  not_submitted: { label: 'Incomplete', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle, dot: 'bg-red-500' },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock, dot: 'bg-amber-500' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2, dot: 'bg-emerald-500' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, dot: 'bg-red-500' },
  clarification_required: { label: 'Clarification Required', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: MessageSquare, dot: 'bg-orange-500' },
};

const CATEGORIES = [
  "Food & Beverages", "Kitchen Supplies", "Groceries", "Transport", "Fuel",
  "Utilities", "Maintenance & Repairs", "Office Supplies", "Cleaning Supplies",
  "Housekeeping Supplies", "Guest Amenities", "Laundry", "Security",
  "Communication", "Staff Welfare", "Medical & First Aid", "Events & Decoration",
  "Printing & Stationery", "Miscellaneous", "Other"
];

// ===== IMAGE QUALITY CHECK (CLIENT-SIDE) =====
function checkImageQuality(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve({ ok: true }); return; }
    // Timeout: don't hang on large files
    const timeout = setTimeout(() => resolve({ ok: true }), 8000);
    const img = new Image();
    img.onload = () => {
      clearTimeout(timeout);
      const w = img.width, h = img.height;
      if (w < 200 || h < 200) { resolve({ ok: false, reason: 'Image resolution too low. Minimum 200x200 pixels required.' }); return; }
      // Resize to max 600px for analysis (avoid mobile memory crash)
      const maxDim = 600;
      let sw = w, sh = h;
      if (w > maxDim || h > maxDim) {
        const scale = maxDim / Math.max(w, h);
        sw = Math.round(w * scale);
        sh = Math.round(h * scale);
      }
      try {
        const canvas = document.createElement('canvas');
        canvas.width = sw; canvas.height = sh;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, sw, sh);
        const imageData = ctx.getImageData(0, 0, sw, sh).data;
        // Brightness check (sample every 32nd pixel for speed)
        let totalBright = 0, samples = 0;
        for (let i = 0; i < imageData.length; i += 128) {
          totalBright += (imageData[i] + imageData[i+1] + imageData[i+2]) / 3;
          samples++;
        }
        const avgBright = totalBright / samples;
        if (avgBright < 20) { resolve({ ok: false, reason: 'Image is too dark. Please retake in better lighting.' }); return; }
        resolve({ ok: true });
      } catch { resolve({ ok: true }); }
    };
    img.onerror = () => { clearTimeout(timeout); resolve({ ok: true }); };
    img.src = URL.createObjectURL(file);
  });
}

// ===== MAIN PAGE =====
export default function PettyCashTeamPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list'); // list | expenses | addExpense | editExpense | submit | manager
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [managerView, setManagerView] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [lookups, setLookups] = useState({ properties: [], vendors: [], purchaseOrders: [], clients: [], categories: [] });
  const [permissions, setPermissions] = useState(null);
  const [oopItems, setOopItems] = useState([]);
  const [rejectedDismissed, setRejectedDismissed] = useState(false);
  const [oopSummary, setOopSummary] = useState({});
  const [editingOOP, setEditingOOP] = useState(null);
  const [claims, setClaims] = useState([]);
  const [claimsSummary, setClaimsSummary] = useState({});
  const isManager = permissions?.can_approve || permissions?.can_view_all_members;

  const fetchOOP = useCallback(async () => {
    try {
      const { data } = await api.get('/petty-cash-team/my-out-of-pocket');
      setOopItems(data.items || []);
      setOopSummary(data.summary || {});
    } catch { /* ignore */ }
  }, []);

  const fetchClaims = useCallback(async () => {
    try {
      const { data } = await api.get('/petty-cash-team/out-of-pocket-claims?status=pending');
      setClaims(data.items || []);
      setClaimsSummary(data.summary || {});
    } catch { /* ignore */ }
  }, []);

  const fetchTransfers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/petty-cash-team/my-transfers');
      setTransfers(data.transfers || []);
      setSummary(data.summary || {});
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const [props, vendors, pos, clients, cats, permsRes] = await Promise.all([
        api.get('/petty-cash-team/lookup/properties').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/vendors').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/purchase-orders').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/clients').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/categories').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/my-permissions').catch(() => ({ data: { permissions: null } })),
      ]);
      setLookups({
        properties: props.data || [],
        vendors: vendors.data || [],
        purchaseOrders: pos.data || [],
        clients: clients.data || [],
        categories: cats.data || [],
      });
      if (permsRes.data?.permissions) setPermissions(permsRes.data.permissions);
    } catch { /* silent */ }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    try {
      const { data } = await api.get('/petty-cash-team/submissions');
      setSubmissions(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user) { fetchTransfers(); fetchLookups(); fetchOOP(); if (isManager) fetchSubmissions(); }
  }, [user, fetchTransfers, fetchSubmissions, fetchLookups, fetchOOP, isManager]);

  const openExpenses = async (transfer) => {
    try {
      // Fetch fresh transfer data and expenses together
      const [transfersRes, expensesRes] = await Promise.all([
        api.get('/petty-cash-team/my-transfers'),
        api.get(`/petty-cash-team/transfer/${transfer.id}/expenses`)
      ]);
      const freshTransfers = transfersRes.data.transfers || [];
      const freshTransfer = freshTransfers.find(t => t.id === transfer.id) || transfer;
      setTransfers(freshTransfers);
      setSummary(transfersRes.data.summary || {});
      setSelectedTransfer(freshTransfer);
      setExpenses(expensesRes.data.expenses || []);
    } catch {
      setSelectedTransfer(transfer);
      toast.error('Failed to load expenses');
    }
    setActiveView('expenses');
  };

  const goBack = () => {
    if (activeView === 'addExpense' || activeView === 'editExpense' || activeView === 'submit') {
      openExpenses(selectedTransfer);
    } else if (activeView === 'carryForward') {
      openExpenses(selectedTransfer);
    } else if (activeView === 'addOOP' || activeView === 'editOOP' || activeView === 'claims') {
      setActiveView('list'); setEditingOOP(null); fetchOOP();
    } else if (activeView === 'memberDetail') {
      setActiveView('list'); setSelectedMember(null); setMemberDetails(null);
    } else if (activeView === 'transferCash' || activeView === 'roleAccess') {
      setActiveView('list');
    } else {
      setActiveView('list'); setSelectedTransfer(null); setManagerView(null);
      fetchTransfers(); if (isManager) fetchSubmissions();
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-sm text-center">
          <Wallet className="h-12 w-12 mx-auto text-[#1B3A5C] mb-4" />
          <h1 className="text-lg font-bold text-[#1B3A5C] mb-2">Petty Cash Team</h1>
          <p className="text-sm text-slate-500 mb-6">Please login to continue</p>
          <button onClick={() => navigate('/login?redirect=/petty-cash-team')}
            className="w-full bg-[#1B3A5C] text-white py-3 rounded-xl font-medium" data-testid="login-redirect-btn">
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="petty-cash-team-page">
      {/* HEADER */}
      <header className="bg-[#1B3A5C] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          {activeView !== 'list' && (
            <button onClick={goBack} className="p-1" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></button>
          )}
          <Wallet className="h-5 w-5" />
          <span className="font-semibold text-sm">Petty Cash</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs opacity-80">{user.full_name}</span>
          <button onClick={() => { logout(); navigate('/login'); }} className="p-1 opacity-70 hover:opacity-100"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <main className="max-w-lg mx-auto pb-24">
        {activeView === 'list' && <TransferList transfers={transfers} summary={summary} loading={loading}
          onViewExpenses={openExpenses} isManager={isManager} submissions={submissions}
          permissions={permissions}
          oopItems={oopItems} oopSummary={oopSummary}
          onAddOOP={() => { setEditingOOP(null); setActiveView('addOOP'); }}
          onEditOOP={(e) => { setEditingOOP(e); setActiveView('editOOP'); }}
          onDeleteOOP={async (id) => {
            try { await api.delete(`/petty-cash-team/out-of-pocket/${id}`); toast.success('Deleted'); await fetchOOP(); }
            catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
          }}
          onViewClaims={async () => { await fetchClaims(); setActiveView('claims'); }}
          claimsSummary={claimsSummary}
          onViewSubmission={(s) => { setManagerView(s); setActiveView('managerDetail'); }}
          onManagerTab={() => setActiveView('managerList')}
          onTransferCash={() => setActiveView('transferCash')}
          onRoleAccess={() => setActiveView('roleAccess')}
          onViewMember={async (member) => {
            setSelectedMember(member);
            try {
              const { data } = await api.get(`/petty-cash-team/admin/member/${member.user_id}/details`);
              setMemberDetails(data);
            } catch { toast.error('Failed to load details'); }
            setActiveView('memberDetail');
          }} />}

        {activeView === 'memberDetail' && memberDetails && <MemberDetail details={memberDetails} member={selectedMember} onBack={goBack}
          onViewSubmission={(s) => { setManagerView(s); setActiveView('managerDetail'); }} />}

        {activeView === 'managerList' && <ManagerList submissions={submissions} onView={(s) => { setManagerView(s); setActiveView('managerDetail'); }} onBack={goBack} onRefresh={fetchSubmissions} />}

        {activeView === 'managerDetail' && managerView && <ManagerDetail submission={managerView} onBack={goBack} onAction={async () => { await fetchSubmissions(); goBack(); }} />}

        {activeView === 'claims' && <ClaimsView claims={claims} summary={claimsSummary} onBack={goBack}
          onReimburse={async (id) => {
            try { const { data } = await api.post(`/petty-cash-team/out-of-pocket/${id}/reimburse`); toast.success(data.message); await fetchClaims(); await fetchTransfers(); }
            catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
          }} />}

        {activeView === 'expenses' && selectedTransfer && <ExpensesList transfer={selectedTransfer} expenses={expenses}
          onAdd={() => { setEditingExpense(null); setActiveView('addExpense'); }}
          onEdit={(e) => { setEditingExpense(e); setActiveView('editExpense'); }}
          onCarryForward={() => setActiveView('carryForward')}
          onDelete={async (id) => {
            try { await api.delete(`/petty-cash-team/expense/${id}`); toast.success('Deleted');
              const { data } = await api.get(`/petty-cash-team/transfer/${selectedTransfer.id}/expenses`);
              setExpenses(data.expenses || []);
            } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
          }}
          onSubmit={() => setActiveView('submit')}
          onResubmit={() => setActiveView('submit')} />}

        {activeView === 'carryForward' && selectedTransfer &&
          <CarryForwardPicker source={selectedTransfer} transfers={transfers} onBack={() => openExpenses(selectedTransfer)}
            onConfirm={async (targetId) => {
              try {
                const { data } = await api.post(`/petty-cash-team/transfer/${selectedTransfer.id}/carry-forward`, { target_transfer_id: targetId });
                toast.success(data.message);
                await fetchTransfers();
                openExpenses(selectedTransfer);
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
            }} />}

        {(activeView === 'addExpense' || activeView === 'editExpense') && selectedTransfer &&
          <ExpenseForm transfer={selectedTransfer} expense={editingExpense} lookups={lookups} onSaved={async () => {
            const { data } = await api.get(`/petty-cash-team/transfer/${selectedTransfer.id}/expenses`);
            setExpenses(data.expenses || []);
            openExpenses(selectedTransfer);
          }} />}

        {(activeView === 'addOOP' || activeView === 'editOOP') &&
          <ExpenseForm oopMode expense={editingOOP} lookups={lookups} onSaved={async () => {
            await fetchOOP(); setActiveView('list');
          }} />}

        {activeView === 'submit' && selectedTransfer &&
          <SubmitView transfer={selectedTransfer} expenses={expenses} onDone={async () => {
            await fetchTransfers(); goBack();
          }} />}

        {activeView === 'transferCash' && <TransferCashForm onDone={async () => { await fetchTransfers(); setActiveView('list'); }} />}

        {activeView === 'roleAccess' && <RoleAccessSettings onBack={goBack} />}
      </main>
    </div>
  );
}

// ===== TRANSFER LIST (HOME) =====
function TransferList({ transfers, summary, loading, onViewExpenses, isManager, submissions, permissions, oopItems, oopSummary, onAddOOP, onEditOOP, onDeleteOOP, onViewClaims, claimsSummary, onViewSubmission, onManagerTab, onTransferCash, onRoleAccess, onViewMember }) {
  const [tab, setTab] = useState('my');
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const pendingCount = submissions.filter(s => s.status === 'pending_approval').length;

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const { data } = await api.get('/petty-cash-team/admin/overview');
      setOverviewData(data);
    } catch { /* ignore */ }
    finally { setOverviewLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'overview' && !overviewData) fetchOverview();
  }, [tab, overviewData, fetchOverview]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3A5C]" /></div>;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="transfer-list">
      {/* Tabs for manager */}
      {isManager && (
        <div className="flex bg-white rounded-xl overflow-hidden border" data-testid="role-tabs">
          <button className={`flex-1 py-2.5 text-xs font-medium transition ${tab === 'overview' ? 'bg-[#1B3A5C] text-white' : 'text-slate-600'}`}
            onClick={() => setTab('overview')} data-testid="overview-tab">Overview</button>
          <button className={`flex-1 py-2.5 text-xs font-medium transition ${tab === 'my' ? 'bg-[#1B3A5C] text-white' : 'text-slate-600'}`}
            onClick={() => setTab('my')}>My Cash</button>
          <button className={`flex-1 py-2.5 text-xs font-medium transition relative ${tab === 'approvals' ? 'bg-[#1B3A5C] text-white' : 'text-slate-600'}`}
            onClick={() => setTab('approvals')} data-testid="approvals-tab">
            Approvals
            {pendingCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center">{pendingCount}</span>}
          </button>
        </div>
      )}

      {tab === 'overview' ? (
        <>
          {/* Action buttons for admin */}
          <div className="flex gap-2">
            {permissions?.can_transfer && (
              <button onClick={onTransferCash} className="flex-1 bg-[#1B3A5C] text-white py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 active:scale-[0.98]" data-testid="mobile-transfer-btn">
                <Send className="h-3.5 w-3.5" />Transfer Cash
              </button>
            )}
            {permissions?.can_transfer && (
              <button onClick={onRoleAccess} className="bg-slate-100 text-slate-700 py-2.5 px-3 rounded-xl text-xs font-medium flex items-center gap-1.5 active:scale-[0.98]" data-testid="role-access-btn">
                <Users className="h-3.5 w-3.5" />Settings
              </button>
            )}
          </div>
          {permissions?.can_transfer && (
            <button onClick={onViewClaims} className="w-full bg-amber-50 border border-amber-200 text-amber-800 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.98]" data-testid="reimbursements-btn">
              <HandCoins className="h-4 w-4" />
              Out-of-Pocket Reimbursements
              {(claimsSummary?.pending_count || 0) > 0 && (
                <span className="bg-amber-500 text-white text-[10px] rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">{claimsSummary.pending_count}</span>
              )}
            </button>
          )}
          <AdminOverview data={overviewData} loading={overviewLoading} onViewMember={onViewMember} onRefresh={fetchOverview} />
        </>
      ) : tab === 'my' ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-2" data-testid="summary-cards">
            <div className="bg-white rounded-xl p-3 border text-center">
              <p className="text-lg font-bold text-[#1B3A5C]">₹{(summary.total_received || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Received</p>
            </div>
            <div className="bg-white rounded-xl p-3 border text-center">
              <p className="text-lg font-bold text-emerald-600">₹{(summary.total_spent || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Spent</p>
            </div>
            <div className="bg-white rounded-xl p-3 border text-center">
              <p className={`text-lg font-bold ${(summary.total_pending || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                ₹{(summary.total_pending || 0).toLocaleString()}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">Pending</p>
            </div>
          </div>

          {/* Rejected submissions alert */}
          {(() => {
            const rejected = transfers.flatMap(t => t.rejected_expenses || []);
            if (rejected.length === 0 || rejectedDismissed) return null;
            return (
              <div className="bg-red-50 border border-red-300 rounded-xl p-3" data-testid="team-rejected-banner">
                <div className="flex items-start gap-2">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-red-800">
                      {rejected.length} {rejected.length === 1 ? 'expense was' : 'expenses were'} rejected
                    </p>
                    <div className="mt-1.5 space-y-1.5">
                      {rejected.slice(0, 4).map(e => (
                        <div key={e.id} className="text-xs text-red-700" data-testid={`team-rejected-item-${e.id}`}>
                          <span className="font-medium">{e.description || 'Expense'}</span>
                          <span> · ₹{(e.amount || 0).toLocaleString()}</span>
                          {e.rejection_reason && <p className="italic text-red-600">Reason: {e.rejection_reason}</p>}
                        </div>
                      ))}
                    </div>
                    {rejected.length > 4 && (
                      <p className="text-[10px] text-red-600 mt-1">+{rejected.length - 4} more — open a transfer to view.</p>
                    )}
                  </div>
                  <button onClick={() => setRejectedDismissed(true)} className="text-red-500" data-testid="team-dismiss-rejected">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Transfer Cards */}
          {transfers.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Wallet className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No petty cash received yet</p>
            </div>
          ) : transfers.map(t => <TransferCard key={t.id} transfer={t} onView={() => onViewExpenses(t)} />)}

          {/* Out of Pocket / Reimbursements */}
          <OutOfPocketSection items={oopItems} summary={oopSummary} onAdd={onAddOOP} onEdit={onEditOOP} onDelete={onDeleteOOP} />
        </>
      ) : (
        <ManagerList submissions={submissions} onView={onViewSubmission} onBack={() => setTab('my')} inline />
      )}
    </div>
  );
}

// ===== SINGLE TRANSFER CARD =====
function TransferCard({ transfer: t, onView }) {
  const sc = STATUS_CONFIG[t.team_status] || STATUS_CONFIG.not_submitted;
  const StatusIcon = sc.icon;
  const isComplete = t.balance_pending <= 0 && t.team_status !== 'not_submitted';
  const canAddExpenses = t.team_status !== 'approved';
  const borderColor = t.team_status === 'approved' ? 'border-emerald-400' :
    t.team_status === 'not_submitted' && t.balance_pending > 0 ? 'border-red-300' :
    t.team_status === 'pending_approval' ? 'border-amber-300' : 'border-slate-200';

  return (
    <div className={`bg-white rounded-xl border-l-4 ${borderColor} shadow-sm overflow-hidden`}
      data-testid={`transfer-card-${t.id}`}>
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="text-xs text-slate-500">{new Date(t.received_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            <p className="text-xs text-slate-400 mt-0.5">From: {t.from_user_name}</p>
          </div>
          <span className={`text-[10px] px-2 py-1 rounded-full font-medium flex items-center gap-1 border ${sc.color}`}>
            <StatusIcon className="h-3 w-3" />{sc.label}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div>
            <p className="text-[10px] text-slate-500">Received</p>
            <p className="text-sm font-bold text-[#1B3A5C]">₹{t.received_amount.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500">Spent</p>
            <p className="text-sm font-bold text-emerald-600">₹{t.total_expenses_recorded.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500">Balance</p>
            <p className={`text-sm font-bold ${t.balance_pending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              ₹{t.balance_pending.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
          <div className={`h-full rounded-full transition-all ${isComplete ? 'bg-emerald-500' : 'bg-[#1B3A5C]'}`}
            style={{ width: `${Math.min(100, (t.total_expenses_recorded / t.received_amount) * 100)}%` }} />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {canAddExpenses && t.balance_pending > 0 ? (
            <button onClick={onView}
              className="flex-1 bg-[#1B3A5C] text-white py-2.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
              data-testid={`add-expense-card-btn-${t.id}`}>
              <Plus className="h-3.5 w-3.5" />Add Expense
            </button>
          ) : (
            <button onClick={onView}
              className="flex-1 bg-slate-100 text-slate-700 py-2.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
              data-testid={`view-card-btn-${t.id}`}>
              <Eye className="h-3.5 w-3.5" />View Details
            </button>
          )}
          {t.expense_count > 0 && canAddExpenses && t.balance_pending > 0 && (
            <button onClick={onView}
              className="bg-slate-100 text-slate-700 py-2.5 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 active:scale-[0.98]">
              <Eye className="h-3.5 w-3.5" />{t.expense_count}
            </button>
          )}
        </div>
        {canAddExpenses && t.balance_pending > 0 && (
          <p className="text-[10px] text-red-500 mt-2 text-center font-medium">
            ₹{t.balance_pending.toLocaleString()} remaining — tap to record expenses
          </p>
        )}
      </div>
    </div>
  );
}

// ===== EXPENSES LIST =====
function ExpensesList({ transfer: t, expenses, onAdd, onEdit, onDelete, onSubmit, onResubmit, onCarryForward }) {
  const sc = STATUS_CONFIG[t.team_status] || STATUS_CONFIG.not_submitted;
  const canEdit = t.team_status !== 'approved';
  const canSubmit = t.team_status === 'not_submitted' && expenses.length > 0;
  const canResubmit = ['clarification_required', 'rejected'].includes(t.team_status);
  const [confirmDel, setConfirmDel] = useState(null);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="expenses-list">
      {/* Transfer summary */}
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs text-slate-500">{new Date(t.received_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
          <span className={`text-[10px] px-2 py-1 rounded-full font-medium border ${sc.color}`}>{sc.label}</span>
        </div>
        {(t.carried_in_amount > 0) && (
          <div className="mb-2 flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5" data-testid="carried-in-line">
            <ArrowRightLeft className="h-3.5 w-3.5 text-blue-600" />
            <p className="text-[11px] text-blue-700">Transfer from previous closing balance: <span className="font-bold">+₹{t.carried_in_amount.toLocaleString()}</span></p>
          </div>
        )}
        {(t.carried_out_amount > 0) && (
          <div className="mb-2 flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5" data-testid="carried-out-line">
            <ArrowRightLeft className="h-3.5 w-3.5 text-slate-500" />
            <p className="text-[11px] text-slate-600">Carried forward to another transfer: <span className="font-bold">−₹{t.carried_out_amount.toLocaleString()}</span></p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div><p className="text-[10px] text-slate-500">Available</p><p className="font-bold text-[#1B3A5C]">₹{(t.effective_received ?? t.received_amount).toLocaleString()}</p></div>
          <div><p className="text-[10px] text-slate-500">Spent</p><p className="font-bold text-emerald-600">₹{t.total_expenses_recorded.toLocaleString()}</p></div>
          <div><p className="text-[10px] text-slate-500">Balance</p><p className={`font-bold ${t.balance_pending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{t.balance_pending.toLocaleString()}</p></div>
        </div>
      </div>

      {/* Clarification/Rejection message */}
      {t.team_status === 'clarification_required' && t.clarification_note && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
          <p className="text-xs font-medium text-orange-700 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />Manager's Note:</p>
          <p className="text-sm text-orange-800 mt-1">{t.clarification_note}</p>
        </div>
      )}
      {t.team_status === 'rejected' && t.rejection_reason && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-xs font-medium text-red-700 flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />Rejection Reason:</p>
          <p className="text-sm text-red-800 mt-1">{t.rejection_reason}</p>
        </div>
      )}

      {/* Add expense button — always visible when balance > 0 and editable */}
      {canEdit && t.balance_pending > 0 && (
        <>
          {t.team_status === 'pending_approval' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800" data-testid="pending-reopen-note">
              You've submitted this for approval, but ₹{t.balance_pending.toLocaleString()} is still unspent. Add more bills below — your submission will reopen and you can re-submit once done.
            </div>
          )}
          <button onClick={onAdd} className="w-full bg-[#1B3A5C] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition" data-testid="add-expense-btn">
            <Plus className="h-5 w-5" />Add Expense (₹{t.balance_pending.toLocaleString()} remaining)
          </button>
          <button onClick={onCarryForward} className="w-full bg-white border border-[#1B3A5C] text-[#1B3A5C] py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition" data-testid="carry-forward-btn">
            <ArrowRightLeft className="h-4 w-4" />Carry forward ₹{t.balance_pending.toLocaleString()} to another transfer
          </button>
        </>
      )}
      {canEdit && t.balance_pending <= 0 && expenses.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
          <p className="text-xs font-medium text-emerald-700">All ₹{(t.effective_received ?? t.received_amount).toLocaleString()} accounted for</p>
        </div>
      )}

      {/* Expense list */}
      {expenses.length === 0 ? (
        <div className="text-center py-8 text-slate-400"><FileText className="h-10 w-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No expenses recorded yet</p></div>
      ) : expenses.map(e => (
        <div key={e.id} className="bg-white rounded-xl border shadow-sm p-3" data-testid={`expense-${e.id}`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs bg-slate-100 px-2 py-0.5 rounded font-medium text-slate-600">{e.category}</span>
                <span className="text-[10px] text-slate-400">{new Date(e.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
              </div>
              <p className="font-bold text-[#1B3A5C] mt-1">₹{(e.amount || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-600 mt-0.5">{e.description || e.purpose || ''}</p>
              {e.vendor_name && <p className="text-[10px] text-slate-400">Vendor: {e.vendor_name}</p>}
              {e.property_name && <p className="text-[10px] text-slate-400">Property: {e.property_name}</p>}
              {e.po_number && <p className="text-[10px] text-blue-600">PO: {e.po_number}</p>}
            </div>
            <div className="flex items-center gap-1 ml-2">
              {(e.bill_images?.length > 0 ? e.bill_images : (e.bill_image_url ? [e.bill_image_url] : [])).map((url, i, arr) => (
                <button key={i} onClick={(ev) => { ev.stopPropagation(); viewBill(url); }} className="p-1.5 text-blue-600 flex items-center" title={`View bill ${i + 1}`} data-testid={`view-bill-${e.id}-${i}`}>
                  <Eye className="h-4 w-4" />{arr.length > 1 && <span className="text-[9px] ml-0.5 font-semibold">{i + 1}</span>}
                </button>
              ))}
              {canEdit && (
                <>
                  <button onClick={() => onEdit(e)} className="p-1.5 text-slate-400 hover:text-blue-600"><Edit2 className="h-4 w-4" /></button>
                  <button onClick={() => setConfirmDel(e.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          </div>
          {(e.notes || e.detailed_notes) && <p className="text-[10px] text-slate-500 mt-2 bg-slate-50 p-2 rounded">{e.notes || e.detailed_notes}</p>}

          {/* Delete confirm */}
          {confirmDel === e.id && (
            <div className="mt-2 bg-red-50 p-2 rounded-lg flex items-center justify-between">
              <span className="text-xs text-red-700">Delete this expense?</span>
              <div className="flex gap-2">
                <button onClick={() => setConfirmDel(null)} className="text-xs text-slate-600 px-2 py-1">Cancel</button>
                <button onClick={() => { onDelete(e.id); setConfirmDel(null); }} className="text-xs text-white bg-red-600 px-3 py-1 rounded" data-testid={`confirm-delete-${e.id}`}>Delete</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Submit / Resubmit */}
      {canSubmit && (
        <button onClick={onSubmit} className="w-full bg-emerald-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 active:scale-[0.98]" data-testid="submit-btn">
          <Send className="h-5 w-5" />Submit for Approval
        </button>
      )}
      {canResubmit && (
        <button onClick={onResubmit} className="w-full bg-amber-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 active:scale-[0.98]" data-testid="resubmit-btn">
          <Send className="h-5 w-5" />Resubmit
        </button>
      )}
    </div>
  );
}

// ===== EXPENSE FORM =====
function ExpenseForm({ transfer, expense, lookups: initialLookups, onSaved, oopMode = false }) {
  const isEdit = !!expense;
  const [localLookups, setLocalLookups] = useState(initialLookups);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [form, setForm] = useState({
    amount: expense?.amount || '',
    description: expense?.description || '',
    expense_date: expense?.expense_date || new Date().toISOString().split('T')[0],
    property_id: expense?.property_id || '',
    vendor_id: expense?.vendor_id || '',
    po_id: expense?.po_id || '',
    client_id: expense?.client_id || '',
    category: expense?.category || '',
    other_category: expense?.other_category || '',
    bill_number: expense?.bill_number || '',
    bill_images: expense?.bill_images || [],
    purchased_by: expense?.purchased_by || '',
    notes: expense?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [billError, setBillError] = useState('');
  const [poSearch, setPoSearch] = useState('');
  const fileRef = useRef(null);

  const { properties, vendors, purchaseOrders, clients, categories } = localLookups;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Fetch lookups if not loaded yet
  useEffect(() => {
    setLocalLookups(initialLookups);
    const hasData = initialLookups.vendors?.length > 0 || initialLookups.properties?.length > 0;
    if (!hasData) {
      setLoadingLookups(true);
      Promise.all([
        api.get('/petty-cash-team/lookup/properties').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/vendors').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/purchase-orders').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/clients').catch(() => ({ data: [] })),
        api.get('/petty-cash-team/lookup/categories').catch(() => ({ data: [] })),
      ]).then(([props, vend, pos, cli, cats]) => {
        setLocalLookups({
          properties: props.data || [], vendors: vend.data || [],
          purchaseOrders: pos.data || [], clients: cli.data || [],
          categories: cats.data || [],
        });
      }).finally(() => setLoadingLookups(false));
    }
  }, [initialLookups]);

  // Auto-fill vendor/client from PO selection
  const handlePOChange = (poId) => {
    set('po_id', poId);
    if (poId) {
      const po = purchaseOrders.find(p => p.id === poId);
      if (po) {
        if (po.vendor_id) set('vendor_id', po.vendor_id);
        if (po.client_id) set('client_id', po.client_id);
      }
    }
  };

  const handleBillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBillError('');
    const quality = await checkImageQuality(file);
    if (!quality.ok) { setBillError(quality.reason); e.target.value = ''; return; }
    setUploading(true);
    try {
      const toUpload = await compressImageFile(file);
      const fd = new FormData();
      fd.append('file', toUpload);
      const { data } = await api.post('/petty-cash-team/upload-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, bill_images: [...p.bill_images, data.url] }));
      toast.success('Bill uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removeBill = (idx) => {
    set('bill_images', form.bill_images.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.category) { toast.error('Select a category'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.description) { toast.error('Enter a description'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        bill_image_url: form.bill_images[0] || null,
      };
      if (oopMode) {
        if (isEdit) {
          await api.put(`/petty-cash-team/out-of-pocket/${expense.id}`, payload);
          toast.success('Out-of-pocket expense updated');
        } else {
          await api.post('/petty-cash-team/out-of-pocket', payload);
          toast.success('Out-of-pocket expense submitted for reimbursement');
        }
      } else if (isEdit) {
        await api.put(`/petty-cash-team/expense/${expense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await api.post(`/petty-cash-team/transfer/${transfer.id}/expense`, payload);
        toast.success('Expense added');
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border rounded-lg px-3 py-2.5 text-sm bg-white";

  return (
    <div className="px-4 py-4 space-y-3" data-testid="expense-form">
      <h2 className="font-semibold text-[#1B3A5C]">{oopMode ? (isEdit ? 'Edit Out-of-Pocket Expense' : 'Add Out-of-Pocket Expense') : (isEdit ? 'Edit Expense' : 'Record Expense')}</h2>
      {oopMode ? (
        <p className="text-xs text-slate-500">Money you spent from your own pocket. This becomes a reimbursement claim for the admin to pay back.</p>
      ) : (
        <p className="text-xs text-slate-500">Against ₹{(transfer.effective_received ?? transfer.received_amount).toLocaleString()} received on {new Date(transfer.received_date).toLocaleDateString('en-IN')}</p>
      )}

      {loadingLookups && <p className="text-xs text-amber-600 animate-pulse">Loading form data...</p>}

      {/* Amount + Date row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">Amount (₹) *</label>
          <input type="number" inputMode="decimal" value={form.amount} onChange={e => set('amount', e.target.value)}
            placeholder="0.00" className={inputCls} data-testid="expense-amount" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">Date *</label>
          <input type="date" value={form.expense_date} onChange={e => set('expense_date', e.target.value)}
            className={inputCls} data-testid="expense-date" />
        </div>
      </div>

      {/* Property */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Property</label>
        <select value={form.property_id} onChange={e => set('property_id', e.target.value)} className={inputCls} data-testid="expense-property">
          <option value="">Select property</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Description */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Description *</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)}
          placeholder="Describe the expense clearly..." rows={2} className={inputCls + " resize-none"} data-testid="expense-description" />
      </div>

      {/* Category */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Category *</label>
        <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls} data-testid="expense-category">
          <option value="">Select category</option>
          {(categories.length > 0 ? categories : CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {form.category === 'Other' && (
          <input type="text" value={form.other_category} onChange={e => set('other_category', e.target.value)}
            placeholder="Specify category" className={inputCls + " mt-2"} />
        )}
      </div>

      {/* Vendor */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Vendor</label>
        <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)} className={inputCls} data-testid="expense-vendor">
          <option value="">Select vendor</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>

      {/* Link to PO */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Link to Purchase Order</label>
        <input
          type="text"
          value={poSearch}
          onChange={e => setPoSearch(e.target.value)}
          placeholder="Search PO number or vendor..."
          className={inputCls + " mb-2"}
          data-testid="expense-po-search"
        />
        {(() => {
          const q = poSearch.trim().toLowerCase();
          const filteredPOs = q
            ? purchaseOrders.filter(po => {
                const vName = po.vendor_name || (vendors.find(v => v.id === po.vendor_id)?.name) || '';
                return (po.po_number || '').toLowerCase().includes(q) || vName.toLowerCase().includes(q);
              })
            : purchaseOrders;
          return (
            <select value={form.po_id} onChange={e => handlePOChange(e.target.value)} className={inputCls} data-testid="expense-po">
              <option value="">None</option>
              {form.po_id && !filteredPOs.some(po => po.id === form.po_id) && (() => {
                const sel = purchaseOrders.find(po => po.id === form.po_id);
                const vName = sel ? (sel.vendor_name || (vendors.find(v => v.id === sel.vendor_id)?.name) || '') : '';
                return sel ? <option value={sel.id}>{sel.po_number}{vName ? ` — ${vName}` : ''}</option> : null;
              })()}
              {filteredPOs.map(po => {
                const vName = po.vendor_name || (vendors.find(v => v.id === po.vendor_id)?.name) || '';
                return <option key={po.id} value={po.id}>{po.po_number}{vName ? ` — ${vName}` : ''}</option>;
              })}
            </select>
          );
        })()}
      </div>

      {/* Client */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Client</label>
        <select value={form.client_id} onChange={e => set('client_id', e.target.value)} className={inputCls} data-testid="expense-client">
          <option value="">Select client</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
        </select>
      </div>

      {/* Purchased By */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Purchased By</label>
        <input type="text" value={form.purchased_by} onChange={e => set('purchased_by', e.target.value)}
          placeholder="Who made the purchase?" className={inputCls} data-testid="expense-purchased-by" />
      </div>

      {/* Bill Number + Upload */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Bill Number</label>
        <input type="text" value={form.bill_number} onChange={e => set('bill_number', e.target.value)}
          placeholder="Invoice/bill number" className={inputCls} data-testid="expense-bill-number" />
      </div>

      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Upload Bills</label>
        {form.bill_images.length > 0 && (
          <div className="space-y-1 mb-2">
            {form.bill_images.map((url, i) => (
              <div key={i} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span className="text-xs text-emerald-700 flex-1">Bill {i + 1}</span>
                <button onClick={() => viewBill(url)} className="text-xs text-blue-600 font-medium">View</button>
                <button onClick={() => removeBill(i)} className="text-xs text-red-600 font-medium">Remove</button>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-full border-2 border-dashed border-slate-300 rounded-lg py-3 flex flex-col items-center gap-1 text-slate-500 active:bg-slate-50" data-testid="upload-bill-btn">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
          <span className="text-xs">{uploading ? 'Uploading...' : 'Take Photo / Upload Bill'}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.csv" className="hidden" onChange={handleBillUpload} data-testid="bill-file-input" />
        {billError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2 mt-1">
            <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{billError}</p>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Notes</label>
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
          placeholder="Additional notes for the manager..." rows={2} className={inputCls + " resize-none"} data-testid="expense-notes" />
      </div>

      {/* Save */}
      <button onClick={handleSubmit} disabled={saving}
        className="w-full bg-[#1B3A5C] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]" data-testid="save-expense-btn">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
        {isEdit ? 'Update Expense' : 'Save Expense'}
      </button>
    </div>
  );
}

// ===== SUBMIT VIEW =====
function SubmitView({ transfer: t, expenses, onDone }) {
  const [note, setNote] = useState('');
  const [balanceReason, setBalanceReason] = useState('');
  const [clarResponse, setClarResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isResubmit = ['clarification_required', 'rejected'].includes(t.team_status);
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const balance = t.received_amount - total;

  const handleSubmit = async () => {
    if (balance > 0 && !balanceReason) { toast.error('Please explain the unused balance'); return; }
    setSubmitting(true);
    try {
      if (isResubmit) {
        await api.post(`/petty-cash-team/transfer/${t.id}/resubmit`, { clarification_response: clarResponse || balanceReason || note || 'Resubmitted' });
      } else {
        await api.post(`/petty-cash-team/transfer/${t.id}/submit`, { note, balance_reason: balanceReason });
      }
      toast.success('Submitted for approval');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="px-4 py-4 space-y-4" data-testid="submit-view">
      <h2 className="font-semibold text-[#1B3A5C]">{isResubmit ? 'Resubmit for Approval' : 'Submit for Approval'}</h2>

      <div className="bg-white rounded-xl p-4 border space-y-3">
        <div className="flex justify-between"><span className="text-sm text-slate-500">Received</span><span className="font-bold">₹{t.received_amount.toLocaleString()}</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">Total Expenses ({expenses.length})</span><span className="font-bold text-emerald-600">₹{total.toLocaleString()}</span></div>
        <hr />
        <div className="flex justify-between"><span className="text-sm font-medium">Balance</span><span className={`font-bold ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{balance.toLocaleString()}</span></div>
      </div>

      {t.team_status === 'clarification_required' && t.clarification_note && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
          <p className="text-xs font-medium text-orange-700">Manager asked:</p>
          <p className="text-sm text-orange-800 mt-1">{t.clarification_note}</p>
          <textarea value={clarResponse} onChange={e => setClarResponse(e.target.value)}
            placeholder="Your response..." rows={2} className="w-full border rounded-lg px-3 py-2 text-sm mt-2 resize-none" data-testid="clar-response" />
        </div>
      )}

      {balance > 0 && (
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">Reason for unused balance (₹{balance.toLocaleString()}) *</label>
          <textarea value={balanceReason} onChange={e => setBalanceReason(e.target.value)}
            placeholder="Explain why the full amount wasn't used..." rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" data-testid="balance-reason" />
        </div>
      )}

      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Additional Note</label>
        <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note for manager"
          className="w-full border rounded-lg px-3 py-2.5 text-sm" />
      </div>

      <button onClick={handleSubmit} disabled={submitting}
        className="w-full bg-emerald-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]" data-testid="confirm-submit-btn">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {isResubmit ? 'Resubmit' : 'Submit for Approval'}
      </button>
    </div>
  );
}

// ===== MANAGER LIST =====
function ManagerList({ submissions, onView, onBack, onRefresh, inline }) {
  const pending = submissions.filter(s => s.status === 'pending_approval');
  const others = submissions.filter(s => s.status !== 'pending_approval');

  return (
    <div className={inline ? 'space-y-3' : 'px-4 py-4 space-y-3'} data-testid="manager-list">
      {!inline && <h2 className="font-semibold text-[#1B3A5C]">Pending Approvals</h2>}
      {pending.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No pending submissions</p>}
      {pending.map(s => <SubmissionCard key={s.transfer_id} sub={s} onView={() => onView(s)} />)}
      {others.length > 0 && (
        <>
          <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wide pt-2">Previous</h3>
          {others.map(s => <SubmissionCard key={s.transfer_id} sub={s} onView={() => onView(s)} />)}
        </>
      )}
    </div>
  );
}

function SubmissionCard({ sub: s, onView }) {
  const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.not_submitted;
  return (
    <div className="bg-white rounded-xl border shadow-sm p-3 active:bg-slate-50" onClick={onView} data-testid={`submission-${s.transfer_id}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="font-medium text-sm text-[#1B3A5C]">{s.user_name}</p>
          <p className="text-[10px] text-slate-400">{new Date(s.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded-full font-medium border ${sc.color}`}>{sc.label}</span>
      </div>
      <div className="flex gap-4 mt-2 text-xs text-slate-600">
        <span>Received: ₹{(s.received_amount || 0).toLocaleString()}</span>
        <span>Spent: ₹{(s.total_expenses || 0).toLocaleString()}</span>
        <span>Balance: ₹{(s.balance_pending || 0).toLocaleString()}</span>
      </div>
    </div>
  );
}

// ===== MANAGER DETAIL VIEW =====
function ManagerDetail({ submission: s, onBack, onAction }) {
  const [action, setAction] = useState(null); // approve | reject | clarification
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);
  const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.not_submitted;

  const handleAction = async () => {
    if (!comments.trim()) { toast.error('Please add comments'); return; }
    setProcessing(true);
    try {
      await api.post(`/petty-cash-team/transfer/${s.transfer_id}/${action}`, { comments });
      toast.success(action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Clarification sent');
      onAction();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  return (
    <div className="px-4 py-4 space-y-4" data-testid="manager-detail">
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="font-semibold text-[#1B3A5C]">{s.user_name}</p>
            <p className="text-xs text-slate-500">Submitted: {new Date(s.submitted_at).toLocaleDateString('en-IN')}</p>
          </div>
          <span className={`text-[10px] px-2 py-1 rounded-full font-medium border ${sc.color}`}>{sc.label}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><p className="text-[10px] text-slate-500">Received</p><p className="font-bold text-[#1B3A5C]">₹{(s.received_amount || 0).toLocaleString()}</p></div>
          <div><p className="text-[10px] text-slate-500">Spent</p><p className="font-bold text-emerald-600">₹{(s.total_expenses || 0).toLocaleString()}</p></div>
          <div><p className="text-[10px] text-slate-500">Balance</p><p className={`font-bold ${(s.balance_pending || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{(s.balance_pending || 0).toLocaleString()}</p></div>
        </div>
        {s.balance_reason && <p className="text-xs text-slate-500 mt-2 bg-slate-50 p-2 rounded">Balance reason: {s.balance_reason}</p>}
        {s.clarification_response && <p className="text-xs text-blue-700 mt-2 bg-blue-50 p-2 rounded">User response: {s.clarification_response}</p>}
      </div>

      {/* Expenses breakdown */}
      <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide">Expenses ({s.expense_count})</h3>
      {(s.expenses || []).map(e => (
        <div key={e.id} className="bg-white rounded-xl border p-3">
          <div className="flex justify-between">
            <div>
              <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded">{e.category}</span>
              <p className="font-bold text-[#1B3A5C] mt-1">₹{(e.amount || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-600">{e.purpose}</p>
              {e.vendor_name && <p className="text-[10px] text-slate-400">Paid to: {e.vendor_name}</p>}
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-400">{new Date(e.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
              <div className="flex items-center justify-end gap-1 mt-1">
                {(e.bill_images?.length > 0 ? e.bill_images : (e.bill_image_url ? [e.bill_image_url] : [])).map((url, i, arr) => (
                  <button key={i} onClick={() => viewBill(url)} className="text-[10px] text-blue-600 font-medium flex items-center gap-0.5" title={`View bill ${i + 1}`}>
                    <Eye className="h-3 w-3" />Bill{arr.length > 1 ? ` ${i + 1}` : ''}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {e.detailed_notes && <p className="text-[10px] text-slate-500 mt-2 bg-slate-50 p-2 rounded">{e.detailed_notes}</p>}
        </div>
      ))}

      {/* Actions */}
      {s.status === 'pending_approval' && !action && (
        <div className="flex gap-2" data-testid="manager-actions">
          <button onClick={() => setAction('approve')} className="flex-1 bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-medium active:scale-[0.98]" data-testid="approve-btn">Approve</button>
          <button onClick={() => setAction('reject')} className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-medium active:scale-[0.98]" data-testid="reject-btn">Reject</button>
          <button onClick={() => setAction('clarification')} className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl text-sm font-medium active:scale-[0.98]" data-testid="clarify-btn">Clarify</button>
        </div>
      )}

      {action && (
        <div className="bg-white rounded-xl border p-3 space-y-3">
          <p className="text-sm font-medium text-[#1B3A5C]">
            {action === 'approve' ? 'Approve — Add Comments' : action === 'reject' ? 'Reject — Enter Reason' : 'Request Clarification'}
          </p>
          <textarea value={comments} onChange={e => setComments(e.target.value)}
            placeholder={action === 'approve' ? 'Approval comments...' : action === 'reject' ? 'Reason for rejection...' : 'What needs clarification?'}
            rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" data-testid="action-comments" />
          <div className="flex gap-2">
            <button onClick={() => { setAction(null); setComments(''); }} className="flex-1 border py-2 rounded-lg text-sm">Cancel</button>
            <button onClick={handleAction} disabled={processing}
              className={`flex-1 text-white py-2 rounded-lg text-sm font-medium ${action === 'approve' ? 'bg-emerald-600' : action === 'reject' ? 'bg-red-600' : 'bg-orange-500'}`}
              data-testid="confirm-action-btn">
              {processing ? 'Processing...' : action === 'approve' ? 'Confirm Approve' : action === 'reject' ? 'Confirm Reject' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ===== ADMIN OVERVIEW =====
function AdminOverview({ data, loading, onViewMember, onRefresh }) {
  const [filter, setFilter] = useState('all');

  if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3A5C]" /></div>;

  const s = data.summary;
  const members = data.team_members || [];
  const activity = data.recent_activity || [];

  const filtered = filter === 'all' ? members :
    filter === 'pending' ? members.filter(m => m.pending_count > 0) :
    filter === 'incomplete' ? members.filter(m => m.incomplete_count > 0) :
    filter === 'approved' ? members.filter(m => m.approved_count > 0 && m.incomplete_count === 0 && m.pending_count === 0) :
    members;

  return (
    <div className="space-y-4" data-testid="admin-overview">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-[#1B3A5C] text-white rounded-xl p-3">
          <p className="text-[10px] opacity-70">Total Distributed</p>
          <p className="text-xl font-bold">₹{s.total_distributed.toLocaleString()}</p>
          <p className="text-[10px] opacity-60 mt-1">{s.team_member_count} team members</p>
        </div>
        <div className="bg-white rounded-xl p-3 border">
          <p className="text-[10px] text-slate-500">Total Expenses</p>
          <p className="text-xl font-bold text-emerald-600">₹{s.total_expenses.toLocaleString()}</p>
          <p className="text-[10px] text-slate-400 mt-1">{s.approved_count} approved</p>
        </div>
        <div className="bg-white rounded-xl p-3 border">
          <p className="text-[10px] text-slate-500">Pending Balance</p>
          <p className={`text-xl font-bold ${s.total_pending_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{s.total_pending_balance.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border">
          <p className="text-[10px] text-slate-500">Awaiting Action</p>
          <div className="flex items-baseline gap-2">
            <p className="text-xl font-bold text-amber-600">{s.pending_approvals}</p>
            <span className="text-[10px] text-slate-400">approvals</span>
          </div>
          <p className="text-[10px] text-red-500 mt-0.5">{s.incomplete_count} incomplete</p>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="overview-filters">
        {[
          { key: 'all', label: 'All' },
          { key: 'pending', label: `Pending (${members.filter(m => m.pending_count > 0).length})` },
          { key: 'incomplete', label: `Incomplete (${members.filter(m => m.incomplete_count > 0).length})` },
          { key: 'approved', label: 'Completed' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`text-[11px] px-3 py-1.5 rounded-full whitespace-nowrap font-medium transition ${filter === f.key ? 'bg-[#1B3A5C] text-white' : 'bg-white border text-slate-600'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Team Members */}
      <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5" />Team Members ({filtered.length})
      </h3>
      {filtered.map(m => (
        <div key={m.user_id} className="bg-white rounded-xl border shadow-sm overflow-hidden active:bg-slate-50 transition"
          onClick={() => onViewMember(m)} data-testid={`member-card-${m.user_id}`}>
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-[#1B3A5C] flex items-center justify-center text-white text-xs font-bold">
                  {m.user_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1B3A5C]">{m.user_name}</p>
                  <p className="text-[10px] text-slate-400">{m.transfer_count} transfer{m.transfer_count !== 1 ? 's' : ''} &middot; {m.expense_count} expense{m.expense_count !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </div>

            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="bg-slate-50 rounded-lg py-1.5">
                <p className="text-xs font-bold text-[#1B3A5C]">₹{m.total_received.toLocaleString()}</p>
                <p className="text-[9px] text-slate-400">Received</p>
              </div>
              <div className="bg-slate-50 rounded-lg py-1.5">
                <p className="text-xs font-bold text-emerald-600">₹{m.total_expenses.toLocaleString()}</p>
                <p className="text-[9px] text-slate-400">Spent</p>
              </div>
              <div className="bg-slate-50 rounded-lg py-1.5">
                <p className={`text-xs font-bold ${m.total_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{m.total_balance.toLocaleString()}</p>
                <p className="text-[9px] text-slate-400">Balance</p>
              </div>
            </div>

            {/* Status indicators */}
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {m.pending_count > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">{m.pending_count} Pending</span>}
              {m.incomplete_count > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">{m.incomplete_count} Incomplete</span>}
              {m.approved_count > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">{m.approved_count} Approved</span>}
              {m.rejected_count > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">{m.rejected_count} Rejected</span>}
              {m.clarification_count > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">{m.clarification_count} Clarification</span>}
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${m.total_received > 0 ? Math.min(100, (m.total_expenses / m.total_received) * 100) : 0}%` }} />
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="text-center py-8 text-slate-400"><Users className="h-10 w-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No team members match this filter</p></div>
      )}

      {/* Recent Activity */}
      {activity.length > 0 && (
        <>
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1.5 pt-2">
            <TrendingUp className="h-3.5 w-3.5" />Recent Activity
          </h3>
          <div className="space-y-2">
            {activity.slice(0, 10).map((act, i) => {
              const actionColors = {
                approved: 'text-emerald-600 bg-emerald-50',
                rejected: 'text-red-600 bg-red-50',
                clarification_requested: 'text-orange-600 bg-orange-50',
              };
              const color = actionColors[act.action] || 'text-slate-600 bg-slate-50';
              return (
                <div key={i} className={`rounded-lg p-2.5 ${color}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium">{act.manager_name} {act.action === 'approved' ? 'approved' : act.action === 'rejected' ? 'rejected' : 'requested clarification on'} {act.user_name}'s submission</p>
                      {act.comments && <p className="text-[10px] mt-0.5 opacity-80">"{act.comments}"</p>}
                    </div>
                    <span className="text-[9px] opacity-60 whitespace-nowrap ml-2">
                      {act.action_date ? new Date(act.action_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ===== MEMBER DETAIL VIEW =====
function MemberDetail({ details, member, onBack, onViewSubmission }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!details) return null;
  const { user, transfers } = details;

  const totalReceived = transfers.reduce((s, t) => s + t.received_amount, 0);
  const totalSpent = transfers.reduce((s, t) => s + t.total_expenses, 0);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="member-detail">
      {/* User header */}
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-[#1B3A5C] flex items-center justify-center text-white font-bold">
            {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <div>
            <p className="font-semibold text-[#1B3A5C]">{user.name}</p>
            <p className="text-xs text-slate-400">{user.email}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><p className="text-[10px] text-slate-500">Received</p><p className="font-bold text-[#1B3A5C]">₹{totalReceived.toLocaleString()}</p></div>
          <div><p className="text-[10px] text-slate-500">Spent</p><p className="font-bold text-emerald-600">₹{totalSpent.toLocaleString()}</p></div>
          <div><p className="text-[10px] text-slate-500">Balance</p><p className={`font-bold ${totalReceived - totalSpent > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{(totalReceived - totalSpent).toLocaleString()}</p></div>
        </div>
      </div>

      {/* Transfer cards */}
      <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide">Transfers ({transfers.length}) — Tap to view expenses</h3>
      {transfers.map(t => {
        const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.not_submitted;
        const StatusIcon = sc.icon;
        const isExpanded = expandedId === t.id;
        const borderColor = t.status === 'approved' ? 'border-emerald-400' :
          t.status === 'not_submitted' && t.balance > 0 ? 'border-red-300' :
          t.status === 'pending_approval' ? 'border-amber-300' : 'border-slate-200';

        return (
          <div key={t.id} className={`bg-white rounded-xl border-l-4 ${borderColor} shadow-sm overflow-hidden`} data-testid={`admin-transfer-${t.id}`}>
            {/* Clickable header */}
            <div className="p-3 active:bg-slate-50 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : t.id)}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-xs text-slate-500">{t.received_date ? new Date(t.received_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
                  <p className="text-[10px] text-slate-400">From: {t.from_user_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 border ${sc.color}`}>
                    <StatusIcon className="h-3 w-3" />{sc.label}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><p className="text-[10px] text-slate-500">Received</p><p className="text-sm font-bold text-[#1B3A5C]">₹{t.received_amount.toLocaleString()}</p></div>
                <div><p className="text-[10px] text-slate-500">Spent</p><p className="text-sm font-bold text-emerald-600">₹{t.total_expenses.toLocaleString()}</p></div>
                <div><p className="text-[10px] text-slate-500">Balance</p><p className={`text-sm font-bold ${t.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{t.balance.toLocaleString()}</p></div>
              </div>
              {/* Progress bar */}
              <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-2">
                <div className={`h-full rounded-full ${t.status === 'approved' ? 'bg-emerald-500' : 'bg-[#1B3A5C]'}`}
                  style={{ width: `${Math.min(100, t.received_amount > 0 ? (t.total_expenses / t.received_amount) * 100 : 0)}%` }} />
              </div>
              {!isExpanded && t.expenses?.length > 0 && (
                <p className="text-[10px] text-blue-600 mt-1.5 font-medium">Tap to view {t.expenses.length} expense{t.expenses.length !== 1 ? 's' : ''} with bills</p>
              )}
            </div>

            {/* Expanded expense details */}
            {isExpanded && (
              <div className="border-t bg-slate-50 px-3 py-3 space-y-3">
                {t.balance_reason && (
                  <p className="text-[10px] text-slate-600 bg-amber-50 border border-amber-200 p-2 rounded-lg">Balance reason: {t.balance_reason}</p>
                )}

                {(!t.expenses || t.expenses.length === 0) ? (
                  <p className="text-xs text-slate-400 text-center py-4">No expenses recorded yet</p>
                ) : (
                  <>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{t.expenses.length} Expense{t.expenses.length !== 1 ? 's' : ''}</p>
                    {t.expenses.map(e => (
                      <div key={e.id} className="bg-white rounded-xl border p-3 space-y-2" data-testid={`admin-expense-${e.id}`}>
                        {/* Expense header */}
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] bg-[#1B3A5C] text-white px-2 py-0.5 rounded font-medium">{e.category}</span>
                              <span className="text-[10px] text-slate-400">
                                {new Date(e.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                              {e.status && (
                                <span className={`text-[9px] px-1.5 py-0.5 rounded ${e.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : e.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {e.status}
                                </span>
                              )}
                            </div>
                            <p className="text-base font-bold text-[#1B3A5C] mt-1">₹{(e.amount || 0).toLocaleString()}</p>
                          </div>
                        </div>

                        {/* Description */}
                        {(e.description || e.purpose) && (
                          <p className="text-xs text-slate-700">{e.description || e.purpose}</p>
                        )}

                        {/* Rejection reason */}
                        {e.status === 'rejected' && e.rejection_reason && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-1.5" data-testid={`team-expense-rejection-${e.id}`}>
                            <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-[11px] text-red-700"><span className="font-semibold">Rejected:</span> {e.rejection_reason}</p>
                          </div>
                        )}

                        {/* Details grid */}
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {e.vendor_name && (
                            <div><p className="text-[9px] text-slate-400">Vendor</p><p className="text-[11px] text-slate-700">{e.vendor_name}</p></div>
                          )}
                          {e.property_name && (
                            <div><p className="text-[9px] text-slate-400">Property</p><p className="text-[11px] text-slate-700">{e.property_name}</p></div>
                          )}
                          {e.po_number && (
                            <div><p className="text-[9px] text-slate-400">PO</p><p className="text-[11px] text-blue-600 font-medium">{e.po_number}</p></div>
                          )}
                          {e.client_name && (
                            <div><p className="text-[9px] text-slate-400">Client</p><p className="text-[11px] text-slate-700">{e.client_name}</p></div>
                          )}
                          {e.purchased_by && (
                            <div><p className="text-[9px] text-slate-400">Purchased By</p><p className="text-[11px] text-slate-700">{e.purchased_by}</p></div>
                          )}
                          {e.bill_number && (
                            <div><p className="text-[9px] text-slate-400">Bill No.</p><p className="text-[11px] text-slate-700">{e.bill_number}</p></div>
                          )}
                        </div>

                        {/* Notes */}
                        {(e.notes || e.detailed_notes) && (
                          <div className="bg-slate-50 rounded-lg p-2">
                            <p className="text-[9px] text-slate-400 mb-0.5">Notes</p>
                            <p className="text-[11px] text-slate-600">{e.notes || e.detailed_notes}</p>
                          </div>
                        )}

                        {/* Bills */}
                        {(e.bill_images?.length > 0 || e.bill_image_url) && (
                          <div>
                            <p className="text-[9px] text-slate-400 mb-1">Bills</p>
                            <div className="flex gap-2 flex-wrap">
                              {(e.bill_images || (e.bill_image_url ? [e.bill_image_url] : [])).map((url, i) => (
                                <button key={i} onClick={() => viewBill(url)}
                                  className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5 text-[11px] text-blue-700 font-medium active:bg-blue-100">
                                  <Eye className="h-3.5 w-3.5" />
                                  View Bill {e.bill_images?.length > 1 ? i + 1 : ''}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ===== TRANSFER CASH FORM (MOBILE) =====
function TransferCashForm({ onDone }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ recipient_user_id: '', amount: '', transfer_date: new Date().toISOString().split('T')[0], notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/petty-cash-team/lookup/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!form.recipient_user_id) { toast.error('Select a team member'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter amount'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/petty-cash-team/transfer-cash', { ...form, amount: parseFloat(form.amount) });
      toast.success(data.message);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Transfer failed'); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border rounded-lg px-3 py-2.5 text-sm bg-white";

  return (
    <div className="px-4 py-4 space-y-4" data-testid="transfer-cash-form">
      <h2 className="font-semibold text-[#1B3A5C]">Transfer Cash</h2>

      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Team Member *</label>
        <select value={form.recipient_user_id} onChange={e => setForm({...form, recipient_user_id: e.target.value})} className={inputCls} data-testid="transfer-recipient">
          <option value="">Select team member</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">Amount (₹) *</label>
          <input type="number" inputMode="decimal" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
            placeholder="0" className={inputCls} data-testid="transfer-amount" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">Date</label>
          <input type="date" value={form.transfer_date} onChange={e => setForm({...form, transfer_date: e.target.value})}
            className={inputCls} data-testid="transfer-date" />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">Notes</label>
        <input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
          placeholder="Optional notes" className={inputCls} />
      </div>

      <button onClick={handleSubmit} disabled={saving}
        className="w-full bg-[#1B3A5C] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]" data-testid="confirm-transfer-btn">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        Transfer Cash
      </button>
    </div>
  );
}

// ===== ROLE ACCESS SETTINGS =====
function RoleAccessSettings({ onBack }) {
  const [roleData, setRoleData] = useState(null);
  const [labels, setLabels] = useState({});
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    api.get('/petty-cash-team/role-access').then(r => {
      setRoleData(r.data.roles || []);
      setLabels(r.data.permission_labels || {});
    }).catch(err => toast.error(err.response?.data?.detail || 'Failed to load'));
  }, []);

  const togglePerm = (roleId, perm, value) => {
    setChanges(prev => ({
      ...prev,
      [roleId]: { ...(prev[roleId] || {}), [perm]: value }
    }));
    setRoleData(prev => prev.map(r => r.role_id === roleId ? { ...r, [perm]: value } : r));
  };

  const handleSave = async () => {
    if (Object.keys(changes).length === 0) { toast.info('No changes'); return; }
    setSaving(true);
    try {
      await api.post('/petty-cash-team/role-access', { permissions: changes });
      toast.success('Role access updated');
      setChanges({});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (!roleData) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3A5C]" /></div>;

  const permKeys = Object.keys(labels);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="role-access-settings">
      <h2 className="font-semibold text-[#1B3A5C]">Role Access Settings</h2>
      <p className="text-xs text-slate-500">Configure which roles can perform each action in the Petty Cash Team module.</p>

      {roleData.map(role => (
        <div key={role.role_id} className="bg-white rounded-xl border shadow-sm p-3" data-testid={`role-config-${role.role_id}`}>
          <p className="font-medium text-sm text-[#1B3A5C] mb-2">{role.role_name}</p>
          <div className="space-y-2">
            {permKeys.map(perm => (
              <label key={perm} className="flex items-center justify-between">
                <span className="text-xs text-slate-600">{labels[perm]}</span>
                <input type="checkbox" checked={!!role[perm]}
                  onChange={e => togglePerm(role.role_id, perm, e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#1B3A5C] cursor-pointer"
                  data-testid={`perm-${role.role_id}-${perm}`} />
              </label>
            ))}
          </div>
        </div>
      ))}

      {Object.keys(changes).length > 0 && (
        <button onClick={handleSave} disabled={saving}
          className="w-full bg-emerald-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]" data-testid="save-role-access-btn">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
          Save Changes
        </button>
      )}
    </div>
  );
}


// ===== OUT-OF-POCKET SECTION (member home) =====
function OutOfPocketSection({ items, summary, onAdd, onEdit, onDelete }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const statusBadge = (s) => s === 'reimbursed'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : 'bg-amber-100 text-amber-700 border-amber-300';
  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 mt-2" data-testid="oop-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <HandCoins className="h-4 w-4 text-[#1B3A5C]" />
          <p className="text-sm font-semibold text-[#1B3A5C]">Out of Pocket</p>
        </div>
        {(summary?.pending_amount || 0) > 0 && (
          <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">₹{(summary.pending_amount).toLocaleString()} awaiting reimbursement</span>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mb-3">Spent your own money? Log it here to claim reimbursement from the admin.</p>

      {(items || []).length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-3">No out-of-pocket expenses yet</p>
      ) : (
        <div className="space-y-2 mb-3">
          {items.map(e => (
            <div key={e.id} className="border rounded-lg p-2.5" data-testid={`oop-item-${e.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded font-medium text-slate-600">{e.category}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusBadge(e.reimbursement_status)}`}>
                      {e.reimbursement_status === 'reimbursed' ? 'Reimbursed' : 'Pending'}
                    </span>
                  </div>
                  <p className="font-bold text-[#1B3A5C] mt-1">₹{(e.amount || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-600">{e.description}</p>
                </div>
                {e.reimbursement_status !== 'reimbursed' && (
                  <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => onEdit(e)} className="p-1.5 text-slate-400 hover:text-blue-600"><Edit2 className="h-4 w-4" /></button>
                    <button onClick={() => setConfirmDel(e.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              {confirmDel === e.id && (
                <div className="mt-2 bg-red-50 p-2 rounded-lg flex items-center justify-between">
                  <span className="text-xs text-red-700">Delete this claim?</span>
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmDel(null)} className="text-xs text-slate-600 px-2 py-1">Cancel</button>
                    <button onClick={() => { onDelete(e.id); setConfirmDel(null); }} className="text-xs text-white bg-red-600 px-3 py-1 rounded" data-testid={`confirm-delete-oop-${e.id}`}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button onClick={onAdd} className="w-full border-2 border-dashed border-slate-300 text-[#1B3A5C] py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 active:bg-slate-50" data-testid="add-oop-btn">
        <Plus className="h-4 w-4" />Add Out-of-Pocket Expense
      </button>
    </div>
  );
}

// ===== CARRY FORWARD PICKER =====
function CarryForwardPicker({ source, transfers, onBack, onConfirm }) {
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);
  // Eligible targets: user's other transfers that aren't approved and aren't this one
  const eligible = (transfers || []).filter(t => t.id !== source.id && t.team_status !== 'approved');

  const confirm = async () => {
    if (!target) { toast.error('Select a transfer to carry the balance into'); return; }
    setSaving(true);
    await onConfirm(target);
    setSaving(false);
  };

  return (
    <div className="px-4 py-4 space-y-4" data-testid="carry-forward-view">
      <h2 className="font-semibold text-[#1B3A5C]">Carry Forward Balance</h2>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
        <p className="text-sm text-blue-800">You're carrying forward <span className="font-bold">₹{source.balance_pending.toLocaleString()}</span> left over from the transfer dated {new Date(source.received_date).toLocaleDateString('en-IN')}.</p>
        <p className="text-[11px] text-blue-700 mt-1">This transfer will be settled to ₹0, and the amount will be added to the transfer you choose as "previous closing balance".</p>
      </div>

      {eligible.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">No other open transfers to carry the balance into.</p>
      ) : (
        <div className="space-y-2" data-testid="carry-target-list">
          {eligible.map(t => (
            <button key={t.id} onClick={() => setTarget(t.id)}
              className={`w-full text-left border rounded-xl p-3 transition ${target === t.id ? 'border-[#1B3A5C] bg-blue-50' : 'border-slate-200 bg-white'}`}
              data-testid={`carry-target-${t.id}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs text-slate-500">{new Date(t.received_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  <p className="text-sm font-bold text-[#1B3A5C]">₹{(t.effective_received ?? t.received_amount).toLocaleString()} · balance ₹{t.balance_pending.toLocaleString()}</p>
                </div>
                {target === t.id && <CheckCircle2 className="h-5 w-5 text-[#1B3A5C]" />}
              </div>
            </button>
          ))}
        </div>
      )}

      <button onClick={confirm} disabled={saving || !target || eligible.length === 0}
        className="w-full bg-[#1B3A5C] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]" data-testid="confirm-carry-forward-btn">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRightLeft className="h-5 w-5" />}
        Carry Forward ₹{source.balance_pending.toLocaleString()}
      </button>
    </div>
  );
}

// ===== ADMIN: OUT-OF-POCKET REIMBURSEMENT CLAIMS =====
function ClaimsView({ claims, summary, onBack, onReimburse }) {
  const [busy, setBusy] = useState(null);
  const doReimburse = async (id) => { setBusy(id); await onReimburse(id); setBusy(null); };
  return (
    <div className="px-4 py-4 space-y-4" data-testid="claims-view">
      <h2 className="font-semibold text-[#1B3A5C]">Out-of-Pocket Reimbursements</h2>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="text-sm text-amber-800"><span className="font-bold">{summary?.pending_count || 0}</span> pending claim(s) · <span className="font-bold">₹{(summary?.pending_amount || 0).toLocaleString()}</span> total</p>
        <p className="text-[11px] text-amber-700 mt-1">Reimbursing creates a transfer to the member for the exact amount and marks the claim as paid.</p>
      </div>

      {(claims || []).length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No pending reimbursement claims.</p>
      ) : claims.map(c => (
        <div key={c.id} className="bg-white rounded-xl border shadow-sm p-3" data-testid={`claim-${c.id}`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-sm font-medium text-[#1B3A5C] flex items-center gap-1"><User className="h-3.5 w-3.5" />{c.user_name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-slate-100 px-2 py-0.5 rounded font-medium text-slate-600">{c.category}</span>
                <span className="text-[10px] text-slate-400">{c.expense_date ? new Date(c.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</span>
              </div>
              <p className="font-bold text-[#1B3A5C] mt-1">₹{(c.amount || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-600">{c.description}</p>
              {(c.bill_images?.length > 0 ? c.bill_images : (c.bill_image_url ? [c.bill_image_url] : [])).map((url, i, arr) => (
                <button key={i} onClick={() => viewBill(url)} className="text-xs text-blue-600 mt-1 mr-2 inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />View bill{arr.length > 1 ? ` ${i + 1}` : ''}</button>
              ))}
            </div>
          </div>
          <button onClick={() => doReimburse(c.id)} disabled={busy === c.id}
            className="w-full mt-2 bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]" data-testid={`reimburse-btn-${c.id}`}>
            {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />}
            Reimburse ₹{(c.amount || 0).toLocaleString()}
          </button>
        </div>
      ))}
    </div>
  );
}
